import { useState, useRef, useCallback } from 'react';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { useFormacao, type NovaFormacaoInput } from '@/context/FormacaoContext';

interface Props {
  onClose: () => void;
  onSuccess: (count: number) => void;
}

// ─── Tipos ────────────────────────────────────────────────────────────────────
interface CsvRow { [col: string]: string }

interface AcaoParsed {
  nome: string;
  entidade_formadora: string;
  categoria: string;
  modalidade: string;
  data_inicio: string;
  data_fim: string;
  duracao_horas: number;
  local: string;
  estado: string;
  custo: number;
  financiamento: string;
  descricao: string;
  erros: string[];
  valida: boolean;
}

// ─── Constantes ───────────────────────────────────────────────────────────────
const MODALIDADES_VALIDAS = ['Presencial', 'E-Learning', 'Misto (Blended)', 'Videoconferência'];
const ESTADOS_VALIDOS_DISPLAY = ['Planeada', 'Em Curso', 'Concluída', 'Cancelada'];
const ESTADOS_VALIDOS_DB = ['planeada', 'em_curso', 'concluida', 'cancelada'];

const CATEGORIAS = [
  'Competências Transversais', 'Tecnologias de Informação', 'Gestão e Liderança',
  'Legislação e Regulamentação', 'Financeiro e Contabilidade', 'Saúde e Segurança',
  'Línguas', 'Técnica Especializada', 'Outra',
];

// Campos que podemos mapear
const CAMPOS_DESTINO = [
  { key: 'nome',              label: 'Nome da Ação *',        required: true  },
  { key: 'entidade_formadora',label: 'Entidade Formadora',    required: false },
  { key: 'categoria',         label: 'Área / Categoria',      required: false },
  { key: 'modalidade',        label: 'Modalidade',            required: false },
  { key: 'data_inicio',       label: 'Data de Início',        required: false },
  { key: 'data_fim',          label: 'Data de Fim',           required: false },
  { key: 'duracao_horas',     label: 'Duração (horas)',        required: false },
  { key: 'local',             label: 'Local',                 required: false },
  { key: 'estado',            label: 'Estado',                required: false },
  { key: 'custo',             label: 'Custo (€)',             required: false },
  { key: 'financiamento',     label: 'Financiamento',         required: false },
  { key: 'descricao',         label: 'Descrição',             required: false },
  { key: '--ignorar--',       label: '— Ignorar coluna —',    required: false },
] as const;

type CampoDestino = typeof CAMPOS_DESTINO[number]['key'];

// ─── CSV Parser ───────────────────────────────────────────────────────────────
function parseCsv(text: string): { headers: string[]; rows: CsvRow[] } {
  const sep = text.includes(';') ? ';' : ',';
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = lines[0].split(sep).map((h) => h.trim().replace(/^"|"$/g, ''));
  const rows: CsvRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const vals = lines[i].split(sep).map((v) => v.trim().replace(/^"|"$/g, ''));
    const row: CsvRow = {};
    headers.forEach((h, j) => { row[h] = vals[j] ?? ''; });
    if (Object.values(row).some((v) => v !== '')) rows.push(row);
  }
  return { headers, rows };
}

// ─── Auto-mapeamento de cabeçalhos ───────────────────────────────────────────
function autoMap(headers: string[]): Record<string, CampoDestino> {
  const mapping: Record<string, CampoDestino> = {};
  const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
  const matchers: [RegExp, CampoDestino][] = [
    [/nome|titulo|title|acao|formacao/,     'nome'              ],
    [/entidade|formadora|provider/,         'entidade_formadora'],
    [/categoria|area|tema/,                 'categoria'         ],
    [/modalidade|modo|format/,              'modalidade'        ],
    [/inicio|start|data.?ini/,              'data_inicio'       ],
    [/fim|end|data.?fim|data.?final/,       'data_fim'          ],
    [/hora|horas|duracao|duration/,         'duracao_horas'     ],
    [/local|location|lugar/,                'local'             ],
    [/estado|status|state/,                 'estado'            ],
    [/custo|cost|preco|valor/,              'custo'             ],
    [/financiamento|fund/,                  'financiamento'     ],
    [/descricao|description/,               'descricao'         ],
  ];
  headers.forEach((h) => {
    const n = normalize(h);
    const match = matchers.find(([re]) => re.test(n));
    mapping[h] = match ? match[1] : '--ignorar--';
  });
  return mapping;
}

// ─── Normalizar data ──────────────────────────────────────────────────────────
function normalizeDate(s: string): string {
  if (!s) return '';
  // dd/mm/yyyy ou dd-mm-yyyy
  const pt = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (pt) return `${pt[3]}-${pt[2].padStart(2, '0')}-${pt[1].padStart(2, '0')}`;
  // yyyy-mm-dd (já correto)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return s;
}

// ─── Normalizar estado ───────────────────────────────────────────────────────
function normalizeEstado(s: string): string {
  const n = s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  if (n.includes('conclu')) return 'Concluída';
  if (n.includes('curso') || n.includes('curso') || n.includes('progress')) return 'Em Curso';
  if (n.includes('cancel')) return 'Cancelada';
  return 'Planeada';
}

// ─── Normalizar modalidade ───────────────────────────────────────────────────
function normalizeModalidade(s: string): string {
  const n = s.toLowerCase();
  if (n.includes('presenc')) return 'Presencial';
  if (n.includes('elearning') || n.includes('e-learning') || n.includes('online')) return 'E-Learning';
  if (n.includes('blend') || n.includes('misto')) return 'Misto (Blended)';
  if (n.includes('video') || n.includes('web')) return 'Videoconferência';
  return 'Presencial';
}

// ─── Parser de linha ─────────────────────────────────────────────────────────
function parseRow(row: CsvRow, mapping: Record<string, CampoDestino>): AcaoParsed {
  const get = (key: CampoDestino): string => {
    const col = Object.entries(mapping).find(([, v]) => v === key)?.[0];
    return col ? (row[col] ?? '').trim() : '';
  };

  const erros: string[] = [];
  const nome = get('nome');
  if (!nome) erros.push('Nome da ação em falta');

  const dataInicioRaw = normalizeDate(get('data_inicio'));
  const dataFimRaw    = normalizeDate(get('data_fim'));

  if (dataInicioRaw && !/^\d{4}-\d{2}-\d{2}$/.test(dataInicioRaw)) erros.push(`Data de início inválida: "${get('data_inicio')}"`);
  if (dataFimRaw && !/^\d{4}-\d{2}-\d{2}$/.test(dataFimRaw)) erros.push(`Data de fim inválida: "${get('data_fim')}"`);
  if (dataInicioRaw && dataFimRaw && dataFimRaw < dataInicioRaw) erros.push('Data de fim anterior à de início');

  const horasRaw = get('duracao_horas').replace(',', '.');
  const horas = horasRaw ? parseFloat(horasRaw) : 0;
  if (horasRaw && isNaN(horas)) erros.push(`Duração inválida: "${get('duracao_horas')}"`);

  const custoRaw = get('custo').replace(',', '.').replace(/[€$\s]/g, '');
  const custo = custoRaw ? parseFloat(custoRaw) : 0;
  if (custoRaw && isNaN(custo)) erros.push(`Custo inválido: "${get('custo')}"`);

  const estadoRaw = get('estado');
  const estado = estadoRaw ? normalizeEstado(estadoRaw) : 'Planeada';

  const modalidadeRaw = get('modalidade');
  const modalidade = modalidadeRaw ? normalizeModalidade(modalidadeRaw) : 'Presencial';

  return {
    nome,
    entidade_formadora: get('entidade_formadora'),
    categoria: get('categoria'),
    modalidade,
    data_inicio: dataInicioRaw,
    data_fim: dataFimRaw,
    duracao_horas: isNaN(horas) ? 0 : horas,
    local: get('local'),
    estado,
    custo: isNaN(custo) ? 0 : custo,
    financiamento: get('financiamento'),
    descricao: get('descricao'),
    erros,
    valida: erros.length === 0,
  };
}

// ─── Template CSV ─────────────────────────────────────────────────────────────
function downloadTemplate() {
  const header = 'Nome da Ação;Entidade Formadora;Área/Categoria;Modalidade;Data Início;Data Fim;Horas;Local;Estado;Custo (€);Financiamento;Descrição';
  const exemplo = 'Excel Avançado;INA;Tecnologias de Informação;Presencial;15/03/2026;17/03/2026;21;Sala A;Planeada;1200;Orçamento Municipal;Formação avançada em Excel para gestão municipal';
  const content = '\uFEFF' + header + '\n' + exemplo;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'template-formacoes.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Passos ───────────────────────────────────────────────────────────────────
type Passo = 'upload' | 'mapear' | 'rever' | 'importar';

// ─── Componente ───────────────────────────────────────────────────────────────
export default function ImportarAcoesModal({ onClose, onSuccess }: Props) {
  useModalOverflow();
  const { animating, handleClose } = useModalAnimation(onClose);
  const { criarFormacao } = useFormacao();

  const [passo, setPasso]       = useState<Passo>('upload');
  const [drag, setDrag]         = useState(false);
  const [headers, setHeaders]   = useState<string[]>([]);
  const [rows, setRows]         = useState<CsvRow[]>([]);
  const [mapping, setMapping]   = useState<Record<string, CampoDestino>>({});
  const [parsed, setParsed]     = useState<AcaoParsed[]>([]);
  const [progresso, setProgresso] = useState(0);
  const [importando, setImportando] = useState(false);
  const [erroGlobal, setErroGlobal] = useState('');
  const [somenteValidas, setSomenteValidas] = useState(true);
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Ler ficheiro ────────────────────────────────────────────────────────────
  const handleFile = useCallback((file: File) => {
    if (!file.name.match(/\.(csv|txt)$/i)) {
      setErroGlobal('Formato não suportado. Use um ficheiro CSV (.csv).');
      return;
    }
    setErroGlobal('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { headers: h, rows: r } = parseCsv(text);
      if (h.length === 0 || r.length === 0) {
        setErroGlobal('O ficheiro está vazio ou não tem dados válidos.');
        return;
      }
      setHeaders(h);
      setRows(r);
      setMapping(autoMap(h));
      setPasso('mapear');
    };
    reader.readAsText(file, 'utf-8');
  }, []);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  }

  // ── Avançar para revisão ─────────────────────────────────────────────────
  function handleMapearContinuar() {
    const temNome = Object.values(mapping).some((v) => v === 'nome');
    if (!temNome) { setErroGlobal('É necessário mapear pelo menos a coluna "Nome da Ação".'); return; }
    setErroGlobal('');
    const p = rows.map((r) => parseRow(r, mapping));
    setParsed(p);
    setPasso('rever');
  }

  // ── Importar ─────────────────────────────────────────────────────────────
  async function handleImportar() {
    const toImport = somenteValidas ? parsed.filter((p) => p.valida) : parsed;
    if (toImport.length === 0) return;

    setImportando(true);
    setProgresso(0);
    setPasso('importar');

    let ok = 0;
    for (let i = 0; i < toImport.length; i++) {
      const a = toImport[i];
      const payload: NovaFormacaoInput = {
        municipio_id: null,
        nome: a.nome,
        entidade_formadora: a.entidade_formadora || null,
        categoria: a.categoria || null,
        modalidade: MODALIDADES_VALIDAS.includes(a.modalidade) ? a.modalidade : 'Presencial',
        data_inicio: a.data_inicio || null,
        data_fim: a.data_fim || null,
        duracao_horas: a.duracao_horas || 0,
        local: a.local || null,
        estado: a.estado,
        custo: a.custo || 0,
        orcamento_previsto: a.custo || 0,
        financiamento: a.financiamento || null,
        descricao: a.descricao || null,
        observacoes: null,
      };

      const result = await criarFormacao(payload);
      if (result) ok++;
      setProgresso(Math.round(((i + 1) / toImport.length) * 100));
    }

    setImportando(false);
    onSuccess(ok);
    onClose();
  }

  const validasCount = parsed.filter((p) => p.valida).length;
  const invalidasCount = parsed.length - validasCount;
  const toImportCount = somenteValidas ? validasCount : parsed.length;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget && !importando) handleClose(); }}
    >
      <div className={`bg-white rounded-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>

        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900 flex-shrink-0">
              <i className="ri-upload-2-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-zinc-900">Importar Ações de Formação</h2>
              <p className="text-[11px] text-zinc-400">Importação via ficheiro CSV</p>
            </div>
          </div>
          <button
            onClick={() => !importando && handleClose()}
            disabled={importando}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer disabled:opacity-30"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-7 py-3 border-b border-zinc-100 flex items-center gap-2 flex-shrink-0">
          {([
            { id: 'upload',  label: '1. Ficheiro'     },
            { id: 'mapear', label: '2. Mapeamento'   },
            { id: 'rever',  label: '3. Revisão'      },
            { id: 'importar', label: '4. Importar'   },
          ] as const).map((s, i, arr) => (
            <div key={s.id} className="flex items-center gap-2">
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all ${
                passo === s.id ? 'bg-zinc-900 text-white' : 'text-zinc-400'
              }`}>{s.label}</span>
              {i < arr.length - 1 && <i className="ri-arrow-right-s-line text-zinc-300 text-sm" />}
            </div>
          ))}
        </div>

        {/* Conteúdo scrollável */}
        <div className="flex-1 overflow-auto px-7 py-5">

          {/* ─ PASSO 1: Upload ─────────────────────────────────────────────── */}
          {passo === 'upload' && (
            <div className="space-y-4">
              <div className="bg-zinc-50 rounded-xl border border-zinc-100 p-4 space-y-2">
                <p className="text-[12px] font-bold text-zinc-700">Formato esperado</p>
                <p className="text-[12px] text-zinc-500">
                  Ficheiro CSV com cabeçalhos na primeira linha. Separador vírgula (,) ou ponto e vírgula (;).
                  Codificação UTF-8 recomendada.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {['Nome da Ação', 'Entidade', 'Área', 'Modalidade', 'Data Início', 'Data Fim', 'Horas', 'Estado'].map((f) => (
                    <span key={f} className="text-[10px] font-semibold bg-white border border-zinc-200 px-2 py-0.5 rounded-full text-zinc-600">{f}</span>
                  ))}
                </div>
                <button
                  onClick={downloadTemplate}
                  className="whitespace-nowrap flex items-center gap-1.5 text-[11px] font-semibold text-zinc-600 hover:text-zinc-900 transition-colors cursor-pointer mt-1"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-download-line text-xs" />
                  </div>
                  Descarregar template de exemplo
                </button>
              </div>

              {erroGlobal && (
                <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-600 text-[12px] px-4 py-3 rounded-xl">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line text-sm" /></div>
                  {erroGlobal}
                </div>
              )}

              <input
                ref={fileRef}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
              />

              <div
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl py-14 flex flex-col items-center gap-3 cursor-pointer transition-all ${
                  drag ? 'border-zinc-500 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50/60'
                }`}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-zinc-100">
                  <i className="ri-file-text-line text-2xl text-zinc-400" />
                </div>
                <div className="text-center">
                  <p className="text-[14px] font-semibold text-zinc-700">Arrasta o ficheiro CSV aqui</p>
                  <p className="text-[12px] text-zinc-400 mt-0.5">ou clica para selecionar</p>
                </div>
                <span className="text-[11px] text-zinc-400 bg-zinc-100 px-3 py-1 rounded-full">.csv · máx. 5 MB</span>
              </div>
            </div>
          )}

          {/* ─ PASSO 2: Mapeamento ──────────────────────────────────────────── */}
          {passo === 'mapear' && (
            <div className="space-y-4">
              <div className="bg-zinc-50 rounded-xl p-3.5 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-zinc-200 flex-shrink-0">
                  <i className="ri-file-text-line text-zinc-500 text-sm" />
                </div>
                <div>
                  <p className="text-[12px] font-bold text-zinc-700">{rows.length} linha(s) detetadas · {headers.length} coluna(s)</p>
                  <p className="text-[11px] text-zinc-400">Associe cada coluna do CSV ao campo correto</p>
                </div>
              </div>

              {erroGlobal && (
                <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-600 text-[12px] px-4 py-3 rounded-xl">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line text-sm" /></div>
                  {erroGlobal}
                </div>
              )}

              <div className="space-y-2">
                {headers.map((h) => {
                  const preview = rows[0]?.[h] ?? '';
                  return (
                    <div key={h} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                      <div className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 min-w-0">
                        <p className="text-[11px] font-bold text-zinc-700 truncate">{h}</p>
                        {preview && <p className="text-[10px] text-zinc-400 truncate mt-0.5">{preview}</p>}
                      </div>
                      <div className="w-5 h-5 flex items-center justify-center text-zinc-300 flex-shrink-0">
                        <i className="ri-arrow-right-line text-sm" />
                      </div>
                      <select
                        value={mapping[h] ?? '--ignorar--'}
                        onChange={(e) => setMapping((prev) => ({ ...prev, [h]: e.target.value as CampoDestino }))}
                        className={`w-full px-2.5 py-2 text-[12px] border rounded-lg bg-white focus:outline-none cursor-pointer transition-colors ${
                          mapping[h] && mapping[h] !== '--ignorar--'
                            ? 'border-zinc-900 text-zinc-900 font-semibold'
                            : 'border-zinc-200 text-zinc-400'
                        }`}
                      >
                        {CAMPOS_DESTINO.map((c) => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─ PASSO 3: Revisão ────────────────────────────────────────────── */}
          {passo === 'rever' && (
            <div className="space-y-4">
              {/* Resumo */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-zinc-50 rounded-xl p-3.5 text-center border border-zinc-100">
                  <p className="text-[22px] font-bold text-zinc-900 tabular-nums">{parsed.length}</p>
                  <p className="text-[11px] text-zinc-500 font-semibold">Total</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3.5 text-center border border-emerald-100">
                  <p className="text-[22px] font-bold text-emerald-700 tabular-nums">{validasCount}</p>
                  <p className="text-[11px] text-emerald-600 font-semibold">Válidas</p>
                </div>
                <div className={`rounded-xl p-3.5 text-center border ${invalidasCount > 0 ? 'bg-rose-50 border-rose-100' : 'bg-zinc-50 border-zinc-100'}`}>
                  <p className={`text-[22px] font-bold tabular-nums ${invalidasCount > 0 ? 'text-rose-600' : 'text-zinc-300'}`}>{invalidasCount}</p>
                  <p className={`text-[11px] font-semibold ${invalidasCount > 0 ? 'text-rose-500' : 'text-zinc-400'}`}>Com erros</p>
                </div>
              </div>

              {invalidasCount > 0 && (
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <div
                    onClick={() => setSomenteValidas((v) => !v)}
                    className={`w-9 h-5 rounded-full transition-colors flex items-center px-0.5 cursor-pointer flex-shrink-0 ${somenteValidas ? 'bg-zinc-900' : 'bg-zinc-200'}`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${somenteValidas ? 'translate-x-4' : 'translate-x-0'}`} />
                  </div>
                  <span className="text-[12px] font-semibold text-zinc-700">
                    Importar só as {validasCount} válidas (ignorar {invalidasCount} com erros)
                  </span>
                </label>
              )}

              {/* Tabela de revisão */}
              <div className="rounded-xl border border-zinc-100 overflow-hidden">
                <div className="max-h-[320px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-zinc-50 z-10">
                      <tr className="border-b border-zinc-100">
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide w-6">#</th>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Nome</th>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-zinc-400 uppercase tracking-wide hidden lg:table-cell">Data</th>
                        <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
                        <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">Válida</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.map((a, i) => (
                        <tr key={i} className={`border-b border-zinc-50 ${!a.valida ? 'bg-rose-50/30' : i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}>
                          <td className="px-4 py-2.5 text-[10px] text-zinc-400 tabular-nums">{i + 1}</td>
                          <td className="px-4 py-2.5">
                            <p className="text-[12px] font-semibold text-zinc-800 max-w-[200px] truncate">{a.nome || '—'}</p>
                            {a.erros.length > 0 && (
                              <div className="mt-0.5 space-y-0.5">
                                {a.erros.map((err, j) => (
                                  <p key={j} className="text-[10px] text-rose-500 flex items-center gap-1">
                                    <i className="ri-error-warning-line text-[9px]" />
                                    {err}
                                  </p>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2.5 hidden lg:table-cell">
                            <p className="text-[11px] text-zinc-500">
                              {a.data_inicio ? new Date(a.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                            </p>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              a.estado === 'Concluída' ? 'bg-emerald-50 text-emerald-700' :
                              a.estado === 'Em Curso'  ? 'bg-amber-50 text-amber-700' :
                              'bg-zinc-100 text-zinc-500'
                            }`}>{a.estado}</span>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {a.valida
                              ? <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 mx-auto"><i className="ri-check-line text-emerald-600 text-xs" /></div>
                              : <div className="w-5 h-5 flex items-center justify-center rounded-full bg-rose-100 mx-auto"><i className="ri-close-line text-rose-500 text-xs" /></div>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ─ PASSO 4: A importar ────────────────────────────────────────── */}
          {passo === 'importar' && (
            <div className="flex flex-col items-center gap-6 py-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-zinc-100">
                {importando
                  ? <i className="ri-loader-4-line text-3xl text-zinc-500 animate-spin" />
                  : <i className="ri-checkbox-circle-line text-3xl text-emerald-500" />
                }
              </div>
              <div className="text-center">
                <p className="text-[16px] font-bold text-zinc-800">
                  {importando ? 'A importar ações...' : 'Importação concluída!'}
                </p>
                <p className="text-[13px] text-zinc-400 mt-1">
                  {importando ? `${progresso}% concluído` : `${toImportCount} ações importadas com sucesso`}
                </p>
              </div>
              <div className="w-full max-w-xs">
                <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-zinc-900 transition-all"
                    style={{ width: `${progresso}%` }}
                  />
                </div>
                <p className="text-[11px] text-zinc-400 text-center mt-1.5 tabular-nums">{progresso}%</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer com botões */}
        {passo !== 'importar' && (
          <div className="px-7 py-4 border-t border-zinc-100 flex items-center justify-between gap-3 flex-shrink-0 bg-white">
            <button
              onClick={() => {
                if (passo === 'upload') handleClose();
                else if (passo === 'mapear') setPasso('upload');
                else if (passo === 'rever') setPasso('mapear');
              }}
              className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
            >
              {passo === 'upload' ? 'Cancelar' : '← Anterior'}
            </button>
            <button
              onClick={() => {
                if (passo === 'mapear') handleMapearContinuar();
                else if (passo === 'rever') handleImportar();
              }}
              disabled={passo === 'rever' && toImportCount === 0}
              className={`whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${passo === 'upload' ? 'hidden' : ''}`}
            >
              {passo === 'mapear' && <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line text-sm" /></div>Rever ({rows.length})</>}
              {passo === 'rever'  && <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-2-line text-sm" /></div>Importar {toImportCount} ação(ões)</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
